import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import Language from '@wix/da-ds/pkg/Icons/24x24/Language';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import ListItemRadioButton from '@wix/da-ds/pkg/List/ListItem/dsPresets/ListItemRadioButton';
import Flex from '@wix/da-ds/pkg/Flex';
import { SUPPORTED_LANGUAGES } from '@wix/da-i18next/pkg/constants';
import { getBaseLanguage } from '@wix/da-i18next/pkg/getBaseLanguage';
import { makeSaveLanguageXHRCall } from '../simpleXHR/actionCreators';
import { addErrorNotification } from '../Notifications/redux/actionCreators';
import cn from 'classnames';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';

import s from './LanguageSelector.scss';

export interface Props {
  className?: string;
  withText?: boolean;
}

const LanguageSelector: React.FC<Props> = ({ className, withText = false }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const shortLang = i18n.language.split('-')[0];
  const [selectedLanguage, setSelectedLanguage] = useState(shortLang);

  const label = t('languageSelector.a11y.changeLanguage');

  const openModal = useCallback(() => {
    setSelectedLanguage(shortLang);
    setIsModalOpen(true);
  }, [shortLang]);

  const closeModal = useCallback(() => setIsModalOpen(false), []);

  const handleConfirm = useCallback(() => {
    dispatch(
      makeSaveLanguageXHRCall({
        language: selectedLanguage,
        onSuccess: () => {
          i18n.changeLanguage(selectedLanguage);
          closeModal();
          window.location.reload();
        },
        onError: () => {
          dispatch(
            addErrorNotification(t('languageSelector.error.saveFailed'))
          );
        },
      })
    );
  }, [dispatch, i18n, selectedLanguage, closeModal, t]);

  const currentLang = t(
    `languageSelector.supportedLanguage.${getBaseLanguage(i18n.language)}`
  );

  const isFools = useFlag('april_fools_2026');

  let AVAILABLE_LANGS = SUPPORTED_LANGUAGES;

  if (isFools) {
    AVAILABLE_LANGS = ['en', 'so'];
  }

  return (
    <>
      {withText ? (
        <TextButton
          size="large"
          variant="main"
          icon={Language}
          aria-label={label}
          className={cn(className, s['text'])}
          onClick={openModal}
        >
          {currentLang}
        </TextButton>
      ) : (
        <IconButton
          size="medium"
          icon={Language}
          aria-label={label}
          className={cn(className)}
          onClick={openModal}
        />
      )}

      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        heading={t('languageSelector.modal.title')}
        hasCloseButton
        showHeaderBorder
        showFooterBorder
        footerRight={[
          <TextButton
            key="cancel"
            size="large"
            variant="main"
            onClick={closeModal}
          >
            {t('languageSelector.modal.cancel')}
          </TextButton>,
          <BrandedButton
            key="confirm"
            size="large"
            variant="brand-green"
            onClick={handleConfirm}
          >
            {t('languageSelector.modal.confirm')}
          </BrandedButton>,
        ]}
        contentClassName={s['modal-content']}
      >
        <Flex
          direction="column"
          role="radiogroup"
          aria-label={t('languageSelector.modal.title')}
        >
          {AVAILABLE_LANGS.map(lang => (
            <ListItemRadioButton
              key={lang}
              name="language-preference"
              value={lang}
              label={t(`languageSelector.supportedLanguage.${lang}`)}
              checked={selectedLanguage === lang}
              onChange={() => setSelectedLanguage(lang)}
            />
          ))}
        </Flex>
      </Modal>
    </>
  );
};
LanguageSelector.displayName = 'LanguageSelector';

export default LanguageSelector;
